import { css } from "@compiled/react";
import { useIntl } from "react-intl";
import { useFragment } from "react-relay";
import { graphql } from "relay-runtime";

import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { templateTabbedSectionRelayFragment$key } from "@/__generated__/templateTabbedSectionRelayFragment.graphql.ts";
import {
    Adg4DraggableTab,
    Adg4DraggableTabList,
    Adg4TabPanel,
    Adg4Tabs
} from "@/components/adg4/tabs/src";
import { SanitizedRichText } from "@/components/confluence/util/sanitized-rich-text";
import wacToken from "@/libraries/design/foundations/token/src/wac-token.tsx";

import { tabbedSectionHeading } from "../../../src/template-details-page/template-benefits-section.tsx";
import { TemplateBenefits } from "../components/template-benefits-relay.tsx";
import { TemplateGuide } from "../components/template-guide-relay.tsx";

const tabStyles = css({
    "backgroundColor": "#F0F1F2",
    "alignItems": "center",
    "justifyContent": "center",
    "boxSizing": "border-box",
    "display": "flex",
    "textAlign": "center",
    "textDecoration": "none",
    "padding": "8px 24px 8px 24px",
    "borderRadius": "24px",
    "cursor": "pointer",
    "border": "2px solid #F0F1F2",
    "fontSize": "18px",
    ":hover": {
        // FIXME: GWP-3063: Disabling instead of fixing so we don't deviate from the control visually.
        // eslint-disable-next-line local/ensure-wac-token-usage
        border: "2px solid #0C66E4",
        // FIXME: GWP-3063: Disabling instead of fixing so we don't deviate from the control visually.
        // eslint-disable-next-line local/ensure-wac-token-usage
        backgroundColor: "#E9F2FE",
        // FIXME: GWP-3063: Disabling instead of fixing so we don't deviate from the control visually.
        // eslint-disable-next-line local/ensure-wac-token-usage
        color: "#0C66E4"
    }
});

const tabStylesCfind3646 = css({
    "backgroundColor": wacToken("color.neutral.light200"), // #F0F1F2
    "alignItems": "center",
    "justifyContent": "center",
    "boxSizing": "border-box",
    "display": "flex",
    "textAlign": "center",
    "textDecoration": "none",
    "padding": `${token("space.100")} ${token("space.400")} ${token(
        "space.100"
    )} ${token("space.400")}`,
    "borderRadius": wacToken("corner.radius.pill"),
    "cursor": "pointer",
    "border": `2px solid ${wacToken("color.neutral.light200")}`,
    "fontSize": "18px",
    ":hover": {
        border: `2px solid ${wacToken("color.primary.blue")}`, // #0C66E4
        backgroundColor: wacToken("color.extended.blue100"), // #E9F2FE
        color: wacToken("color.primary.blue") // #0C66E4
    }
});

const currentStyle = css({
    // FIXME: GWP-3063: Disabling instead of fixing so we don't deviate from the control visually.
    // eslint-disable-next-line local/ensure-wac-token-usage
    border: "2px solid #0C66E4",
    // FIXME: GWP-3063: Disabling instead of fixing so we don't deviate from the control visually.
    // eslint-disable-next-line local/ensure-wac-token-usage
    backgroundColor: "#E9F2FE",
    // FIXME: GWP-3063: Disabling instead of fixing so we don't deviate from the control visually.
    // eslint-disable-next-line local/ensure-wac-token-usage
    color: "#0C66E4"
});

const currentStyleCfind3646 = css({
    border: `2px solid ${wacToken("color.primary.blue")}`, // #0C66E4
    backgroundColor: wacToken("color.extended.blue100"), // #E9F2FE
    color: wacToken("color.primary.blue") // #0C66E4
});

const tabSectionStyle = css({
    marginBlockStart: "56px"
});

const sectionStyles = css({
    color: wacToken("color.extended.blue900"),
    paddingBlockStart: "64px",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    [media.below.sm]: {
        overflow: "scroll",
        width: "100%"
    }
});

const sectionStylesCfind3646 = css({
    color: wacToken("color.extended.blue900"),
    paddingBlockStart: "32px",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    [media.below.sm]: {
        overflow: "scroll",
        width: "100%"
    }
});

const styles = css({
    display: "flex",
    [media.above.sm]: {
        justifyContent: "center"
    }
});

const TabHeading = ({
    title,
    isSelected,
    isEnrolled
}: {
    title?: string;
    isSelected?: boolean;
    isEnrolled?: boolean;
}) => {
    return (
        <div
            css={[
                isEnrolled ? tabStylesCfind3646 : tabStyles,
                isEnrolled && isSelected && currentStyleCfind3646,
                !isEnrolled && isSelected && currentStyle
            ]}
            className="h5"
        >
            {title}
        </div>
    );
};

interface TemplateTabbedSectionProps {
    template: templateTabbedSectionRelayFragment$key;
    isCfind3646Treatment: boolean;
}

const TemplateTabbedSectionRelayFragment = graphql`
    fragment templateTabbedSectionRelayFragment on ContentPlatformTemplate {
        templateOverview {
            overviewTitle
            overviewDescription
        }
        templateBenefits {
            ...templateBenefitsRelayFragment
        }
        howToUseThisTemplate {
            ...templateGuideRelayFragment
        }
        templateProductRationale {
            rationaleTitle
            templateProductRationaleDescription
        }
    }
`;

export const TemplateTabbedSection = ({
    template: templateRef,
    isCfind3646Treatment: isCfind3646Treatment
}: TemplateTabbedSectionProps) => {
    const intl = useIntl();

    const template = useFragment(
        TemplateTabbedSectionRelayFragment,
        templateRef
    );

    const tabs = [
        {
            id: "tab1",
            title: intl.formatMessage({
                id: "ConfluenceTemplateMessages.tabs.overview.cfind.1508",
                defaultMessage: "Overview"
            }),
            hide: !template.templateOverview,
            component: (
                <section css={tabSectionStyle}>
                    {template.templateOverview?.map((overview, index) => (
                        <section key={`overview-${index}`}>
                            <h2 css={tabbedSectionHeading}>
                                {overview.overviewTitle}
                            </h2>
                            {overview.overviewDescription && (
                                <SanitizedRichText
                                    innerHTML={overview.overviewDescription}
                                />
                            )}
                        </section>
                    ))}
                </section>
            )
        },
        {
            id: "tab2",
            title: intl.formatMessage({
                id: "ConfluenceTemplateMessages.tabs.benefits.cfind.1508",
                defaultMessage: "Benefits"
            }),
            hide:
                !template.templateBenefits ||
                template.templateBenefits.length === 0,
            component: (
                <section>
                    {template.templateBenefits?.map(
                        (templateBenefitsContainer, index) => (
                            <TemplateBenefits
                                key={`benefits-${index}`}
                                templateBenefits={templateBenefitsContainer}
                            />
                        )
                    )}
                </section>
            )
        },
        {
            id: "tab3",
            title: intl.formatMessage({
                id: "ConfluenceTemplateMessages.tabs.howTo.cfind.1508",
                defaultMessage: "How to"
            }),
            hide:
                !template.howToUseThisTemplate ||
                template.howToUseThisTemplate.length === 0,
            component: (
                <section>
                    {template.howToUseThisTemplate?.map(
                        (templateGuide, index) => (
                            <TemplateGuide
                                key={`how2use-${index}`}
                                templateGuide={templateGuide}
                            />
                        )
                    )}
                </section>
            )
        },
        {
            id: "tab4",
            title: intl.formatMessage({
                id: "ConfluenceTemplateMessages.tabs.whyConfluence.cfind.1508",
                defaultMessage: "Why Confluence?"
            }),
            // temporary content for demoing... will be replaced with content from template object
            hide: !template.templateProductRationale,

            component: (
                <section css={tabSectionStyle}>
                    {template.templateProductRationale?.map(
                        (rationale, index) => (
                            <section key={`rationale-${index}`}>
                                <h2 css={tabbedSectionHeading}>
                                    {rationale.rationaleTitle}
                                </h2>
                                {rationale.templateProductRationaleDescription && (
                                    <SanitizedRichText
                                        innerHTML={
                                            rationale.templateProductRationaleDescription
                                        }
                                    />
                                )}
                            </section>
                        )
                    )}
                </section>
            )
        }
    ];

    const tabsCfind3646 = [tabs[3], tabs[0], tabs[1], tabs[2]];

    const renderTabbedSection = () => {
        if (isCfind3646Treatment) {
            return (
                <Adg4Tabs>
                    <Adg4DraggableTabList css={styles}>
                        {tabsCfind3646
                            .filter((tab) => !tab?.hide)
                            .map((tab) => (
                                <Adg4DraggableTab
                                    key={tab?.id}
                                    title={tab?.title}
                                >
                                    <TabHeading
                                        title={tab?.title}
                                        isEnrolled={isCfind3646Treatment}
                                    />
                                </Adg4DraggableTab>
                            ))}
                    </Adg4DraggableTabList>
                    {tabsCfind3646
                        .filter((tab) => !tab?.hide)
                        .map((tab, index) => (
                            <Adg4TabPanel key={index} keepInDom={true}>
                                <>{tab?.component}</>
                            </Adg4TabPanel>
                        ))}
                </Adg4Tabs>
            );
        } else
            return (
                <Adg4Tabs>
                    <Adg4DraggableTabList css={styles}>
                        {tabs
                            .filter((tab) => !tab.hide)
                            .map((tab) => (
                                <Adg4DraggableTab
                                    key={tab.id}
                                    title={tab.title}
                                >
                                    <TabHeading title={tab.title} />
                                </Adg4DraggableTab>
                            ))}
                    </Adg4DraggableTabList>
                    {tabs
                        .filter((tab) => !tab.hide)
                        .map((tab, index) => (
                            <Adg4TabPanel key={index} keepInDom={true}>
                                <>{tab.component}</>
                            </Adg4TabPanel>
                        ))}
                </Adg4Tabs>
            );
    };

    return (
        <section
            data-testid="template-tabbed-section"
            css={[
                isCfind3646Treatment ? sectionStylesCfind3646 : sectionStyles
            ]}
        >
            {renderTabbedSection()}
        </section>
    );
};
